import * as React from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { Close } from '@mui/icons-material';
import { ListSubheader } from '@mui/material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useTranslation } from 'react-i18next';

type ControlledMultiPickerWithCategoryProps<T> = {
  label: string;
  name: string;
  fetchMethod: () => Promise<T[]>;
  callback: (selected: T[] | undefined) => void;
  showCategory?: boolean;
  componentWidth?: string;
  limitTags?: number;
};

export const ControlledMultiPickerWithCategory = <T,>(props: ControlledMultiPickerWithCategoryProps<T>) => {
  const { label, name, fetchMethod, callback, showCategory = true, componentWidth = '15rem', limitTags = 2 } = props;
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const loading = open && options.length === 0;
  const { t } = useTranslation(['common']);

  const { control } = useFormContext();

  const isMounted = useIsMounted();

  const { errors } = useFormState();

  React.useEffect(() => {
    let active = true;

    if (!loading) {
      return undefined;
    }

    if (!options.length) {
      (async () => {
        const result = await fetchMethod();

        if (active && isMounted()) {
          setOptions([...result]);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [fetchMethod, isMounted, loading, options.length]);

  const componentId = `autocomplete-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  let isError = false;
  let errorMessage = '';

  if (errors && Object.prototype.hasOwnProperty.call(errors, name)) {
    isError = true;
    errorMessage = errors[name]?.message?.toString() || '';
  }

  const filterOptions = createFilterOptions({
    // matchFrom: 'start',
    stringify: (opt: any) => opt.name,
  });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          multiple
          limitTags={limitTags}
          id={componentId}
          filterOptions={filterOptions}
          groupBy={(option) => option.parentName}
          renderGroup={(params) => {
            return (
              <li key={params.key}>
                {showCategory && <ListSubheader component='div'>{params.group}</ListSubheader>}
                <ul
                  {...(!showCategory && {
                    style: {
                      padding: 0,
                      margin: 0,
                    },
                  })}
                >
                  {params.children as any}
                </ul>
              </li>
            );
          }}
          isOptionEqualToValue={(option, val) => {
            if (val.isParent) {
              return !option.isParent && option.parentCode === val.parentCode;
            }
            return !option.isParent && option.code === val.code;
          }}
          options={options}
          autoHighlight
          open={open}
          openOnFocus
          disableCloseOnSelect
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          loading={loading}
          getOptionLabel={(option) => option.name}
          onChange={(event: any, newValue: any) => {
            field.onChange(newValue);
            callback(newValue);
          }}
          value={field.value || []} // Keep value in sync with react-hook-form
          defaultValue={field.value || []} // Set default value for initial render
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              sx={{ width: componentWidth }}
              placeholder={field.value && field.value.length > 0 ? undefined : t('common:form.all')}
              label={label}
              error={isError}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password', // disable autocomplete and autofill
              }}
              helperText={errorMessage ? <em>{errorMessage}</em> : null}
            />
          )}
          clearIcon={<Close fontSize='small' />}
          sx={{
            '& .MuiChip-sizeMedium ': { height: 'auto', borderRadius: 1 },
            '& .MuiChip-deleteIcon ': { fontSize: '18px' },
          }}
        />
      )}
    />
  );
};
